import type { Collectible } from "@/services/collectibles/collectiblesTypes";
import { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { CollectionItemElastic } from "@/services/collection/item/elastic/collectionItemElasticSchema";
import { FoilParameterName, type ItemParameter, type ParameterElasticId, type ParameterValueId } from "@/services/parameters/parametersSchema";
import type { PriceNumeric } from "@/services/prices/priceTypes";

/** A single foil/printing variant of a trading card and its estimated value. */
export type FoilVariant = {
  name: string;
  price: PriceNumeric | null;
};

/** The { keyId, valueId } pair needed to tag a collection item with a foil parameter value. */
export type FoilSelection = { keyId: ParameterElasticId; valueId: ParameterValueId };

/** How many pieces of each foil variant the current user owns, keyed by foil name (e.g. "Holofoil" → 2). */
export type OwnedCountByFoil = Map<string, number>;

/**
 * Trading-card categories whose catalogue prices are keyed by foil/printing (the price `type`,
 * e.g. "Normal" / "Cold Foil"). Their collection items record the printing in the Foil parameter,
 * so the prices double as the variants a user picks between when adding to the collection.
 */
export type FoilCategoryId = CategoryId.Pokemon | CategoryId.PokemonJapan | CategoryId.Lorcana | CategoryId.MagicTheGathering | CategoryId.Riftbound;

const FoilCategoryIds: ReadonlySet<CategoryId> = new Set<FoilCategoryId>([CategoryId.Pokemon, CategoryId.PokemonJapan, CategoryId.Lorcana, CategoryId.MagicTheGathering, CategoryId.Riftbound]);

export const hasFoilVariants = (categoryId: CategoryId): categoryId is FoilCategoryId => FoilCategoryIds.has(categoryId);

/** Narrows a detail-shape collectible to the foil-category branch, which carries `priceVariant` / `ownedCountByFoil`. */
export const isFoilCollectible = (collectible: Collectible): collectible is Extract<Collectible, { categoryId: FoilCategoryId }> => hasFoilVariants(collectible.categoryId);

// Below this, there is no meaningful foil choice, so the plain "add" button is kept.
export const MinFoilVariantsForPicker = 2;

// Items with no Foil parameter are bucketed here (mirrors the backend OwnedCountAggregator default).
export const DefaultFoilName = "Normal";

/** Foil variants of a collectible from the (deprecated) detail shape. Empty outside the foil categories. */
export const getFoilVariantsFromCollectible = (collectible: Collectible): FoilVariant[] => {
  if (!isFoilCollectible(collectible)) {
    return [];
  }

  // priceVariant is a keyed price map at runtime, but the API sends an empty array when there are none.
  return Object.entries(collectible.priceVariant).map(([name, price]) => ({ name, price }));
};

/** Owned pieces per foil variant of a detail-shape collectible. Empty outside the foil categories. */
export const getOwnedCountByFoil = (collectible: Collectible): OwnedCountByFoil => (isFoilCollectible(collectible) ? (collectible.ownedCountByFoil ?? new Map()) : new Map());

export const hasSelectableFoilVariants = (variants: FoilVariant[]): boolean => variants.length >= MinFoilVariantsForPicker;

/**
 * Stepper rows for the add-to-collection dropdown: priced variants plus any owned foil bucket
 * without a listed price, falling back to a single default bucket when there is neither
 * (non-foil collectibles) so the stepper always exists.
 */
export const buildFoilDisplayVariants = (variants: FoilVariant[], ownedFoilNames: Iterable<string>): FoilVariant[] => {
  const variantsByName = new Map<string, FoilVariant>();
  for (const variant of variants) {
    variantsByName.set(variant.name, variant);
  }
  for (const name of ownedFoilNames) {
    if (!variantsByName.has(name)) {
      variantsByName.set(name, { name, price: null });
    }
  }
  if (variantsByName.size === 0) {
    variantsByName.set(DefaultFoilName, { name: DefaultFoilName, price: null });
  }
  return [...variantsByName.values()];
};

/** The printing the item's own Foil parameter names; null when it names none. */
export const findItemFoilName = (item: Pick<CollectionItemElastic, "parameters">): string | null => item.parameters.find((parameter) => parameter.name === FoilParameterName)?.value ?? null;

export const findFoilParameter = (parameters: ItemParameter[]): ItemParameter | undefined => parameters.find((parameter) => parameter.name === FoilParameterName);

/** The variant name matches a foil value name 1:1 (e.g. "Reverse Holofoil"). */
export const findFoilValueId = (foilParameter: ItemParameter, variantName: string): ParameterValueId | undefined => foilParameter.values.find((value) => value.value === variantName)?.id;
